import { useEffect, useState } from "react";
import { isValid, parseJSON } from "date-fns";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import {
  Box,
  Button,
  Divider,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  Symbol,
  SymbolV2,
  CreateOrUpdateWatchlistEntryParams,
  CreateOrUpdateWatchlistEntryParamsTypeEnum,
  useCreateWatchlistEntry,
  useEditWatchlistEntry,
} from "@rentablo/api/apiv1";
import { LoadingButton, ScrollableWithGradients } from "@rentablo/components";
import { WatchlistV2Required } from "@features/watchlists/types";
import { useGetHistoricQuotesLookup } from "@api/v1/hooks/useLookupHistoricQuotes";
import { WatchlistSelector } from "../add/components/WatchlistSelector";
import { SecuritiesValuesFields } from "../add/components/SecuritiesValuesFields";
import { ValuesSelectionChartContainer } from "../add/components/ValuesSelectionChartContainer";

const SecuritiesAddFormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(6)};
`;

export type SecurityMeta = Pick<
  Symbol,
  "name" | "id" | "isin" | "currency" | "mappedType" | "quoteProvider"
>;

const isSymbolV2 = (symbol: Symbol | SymbolV2): symbol is SymbolV2 =>
  "type" in symbol;

interface SecuritiesAddFormProps {
  securityMeta:
    | Symbol
    | (SymbolV2 & { currency: string; tickerSymbol: string });
  watchlists: WatchlistV2Required[];
  currentWatchlist?: WatchlistV2Required;
  securityId?: string;
  purchaseDate?: Date;
  purchasePrice?: number;
  onPrevious?: () => void;
  onSave?: (watchlistId?: number) => void;
}

// FIXME: delete bang operator from the component
export const SecuritiesAddAndEditForm = ({
  securityMeta,
  currentWatchlist,
  watchlists,
  securityId,
  purchasePrice,
  purchaseDate,
  onSave,
  onPrevious,
}: SecuritiesAddFormProps) => {
  const isEdit = Boolean(securityId);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const offsetValue = isSM ? 4 : 5;

  const navigate = useNavigate();

  const [watchlist, setWatchlist] = useState<WatchlistV2Required | undefined>(
    currentWatchlist
  );
  const [date, setDate] = useState<Date | undefined>(
    purchaseDate ?? new Date()
  );
  const [price, setPrice] = useState(purchasePrice ?? 0);
  const [chartRange, setChartRange] = useState<RangeType>();

  useEffect(() => {
    setWatchlist(currentWatchlist);
  }, [currentWatchlist]);

  const { mutateAsync: save, isPending: isCreateSaving } =
    useCreateWatchlistEntry({
      onSuccess: () => {
        enqueueSnackbar({
          message: "Wertpapier wurde erfolgreich hinzugefügt",
          variant: "success",
          autoHideDuration: 3000,
        });
      },
    });

  const { mutateAsync: update, isPending: isUpdateSaving } =
    useEditWatchlistEntry();

  const { data } = useGetHistoricQuotesLookup(
    {
      tickerSymbol: isSymbolV2(securityMeta)
        ? securityMeta.tickerSymbol!
        : securityMeta.id!,
      quoteProvider: securityMeta.quoteProvider!,
      from: chartRange?.startDate?.toISOString(),
      to: chartRange?.endDate?.toISOString(),
    },
    {
      onSuccess: (data) => {
        if (isEdit) return;

        const historicQuotes =
          data?.historicQuotes?.[
            isSymbolV2(securityMeta)
              ? securityMeta.tickerSymbol!
              : securityMeta.id!
          ] ?? [];
        const [lastHistoricQuote] = historicQuotes.slice(-1);

        if (lastHistoricQuote) {
          setDate(parseJSON(lastHistoricQuote.date));
          setPrice(lastHistoricQuote.value);
        }
      },
    }
  );

  const handleSelectWatchlist = (item: WatchlistV2Required) =>
    setWatchlist(item);

  const handleDateChange = (value?: Date) => setDate(value);

  const handlePriceChange = (value: number) => setPrice(value);

  const handleSelectDatetimeRange = (range: RangeType) => setChartRange(range);

  const handleSubmit = async () => {
    const symbolIsV2 = isSymbolV2(securityMeta);

    const params: CreateOrUpdateWatchlistEntryParams = {
      name: securityMeta.name,
      tickerSymbol: symbolIsV2 ? securityMeta.tickerSymbol : securityMeta.id,
      isin: securityMeta.isin,
      currency: securityMeta.currency,
      type: (symbolIsV2
        ? securityMeta.type
        : securityMeta.mappedType) as CreateOrUpdateWatchlistEntryParamsTypeEnum,
      quoteProvider: securityMeta.quoteProvider,
      purchaseDate: date?.toISOString(),
      purchasePrice: price,
      watchlistId: watchlist?.id,
    };

    if (isEdit) {
      await update({
        id: Number.parseInt(securityId!, 10),
        createOrUpdateWatchlistEntryParams: params,
      });
    } else {
      await save({ createOrUpdateWatchlistEntryParams: params });
    }

    const backURL = watchlist?.id
      ? `/watchlists/${watchlist.id}`
      : "/watchlists";

    onSave ? onSave(watchlist?.id) : navigate(backURL);
  };

  const canSubmit =
    Boolean(date) &&
    isValid(date) &&
    price > 0 &&
    !isCreateSaving &&
    !isUpdateSaving;

  const isFetching = isUpdateSaving || isCreateSaving;
  const buttonText = isEdit
    ? t("securitiesAddPage.editButton")
    : t("securitiesAddPage.saveButton");

  const historicQuotes =
    data?.historicQuotes?.[
      isSymbolV2(securityMeta) ? securityMeta.tickerSymbol! : securityMeta.id!
    ] ?? [];

  const fieldsContent = (
    <>
      <Typography variant="h4" color="textPrimary">
        {securityMeta.name}
      </Typography>

      <WatchlistSelector
        watchlists={watchlists}
        currentWatchlist={watchlist}
        onSelect={handleSelectWatchlist}
      />

      <SecuritiesValuesFields
        date={date}
        price={price}
        onDateChange={handleDateChange}
        onPriceChange={handlePriceChange}
      />

      <Divider />

      <ValuesSelectionChartContainer
        historicQuotes={historicQuotes}
        onSelectDatetimeRange={handleSelectDatetimeRange}
        onDateChange={handleDateChange}
        onPriceChange={handlePriceChange}
      />
    </>
  );

  const actionRow = (
    <Box
      display="flex"
      alignItems={{ xs: "stretch", sm: "center" }}
      flexDirection={{ xs: "column", sm: "row" }}
      justifyContent="flex-end"
      gap={2}
    >
      {onPrevious && (
        <Button
          onClick={onPrevious}
          data-testid="actions-previous"
          variant="tertiary"
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {t("navigation.previous")}
        </Button>
      )}
      <LoadingButton
        data-testid="add-now-button"
        variant="primary"
        isFetching={isFetching}
        disabled={!canSubmit}
        onClick={handleSubmit}
        sx={{ width: { xs: "100%", sm: "auto" } }}
      >
        {buttonText}
      </LoadingButton>
    </Box>
  );

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      minHeight={0}
      maxWidth={700}
    >
      <ScrollableWithGradients sx={{ mr: -offsetValue, pr: offsetValue }}>
        <SecuritiesAddFormWrapper>{fieldsContent}</SecuritiesAddFormWrapper>
      </ScrollableWithGradients>
      <Box pt={4} flexShrink={0}>
        {actionRow}
      </Box>
    </Box>
  );
};
